import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { seguirVersaoReconduzida, trocarVersao, versaoDisponivel, versaoReconduzida } from "@/lib/versao";
import {
  Home,
  Users,
  Briefcase,
  FolderKanban,
  Building2,
  Settings,
  Star,
  ShieldCheck,
  Folder,
  FileText,
  UserCheck,
  ClipboardList,
  LogOut,
  Sparkles,
  Mail,
  Factory,
  Sliders,
  Gauge,
  Radar,
  HeartHandshake,
  MessageCircle,
  Landmark,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { useActivityPendingCount } from "@/hooks/use-activity-pending-count";
import type { ScreenType } from "@/types/user-profile";

interface MenuLeaf {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  screenType: ScreenType;
  badgeKey?: string;
}

interface MenuSection {
  /** Rótulo da seção. Vazio = seção sem cabeçalho (ex.: Dashboard solto). */
  label?: string;
  items: MenuLeaf[];
}

const menuSections: MenuSection[] = [
  {
    items: [
      { title: "Dashboard", icon: Home, path: "/dashboard", screenType: "dashboard" },
    ],
  },
  {
    label: "Gestão",
    items: [
      { title: "Freelancers", icon: Users, path: "/freelancers", screenType: "freelancers" },
      { title: "Vagas", icon: Briefcase, path: "/vagas", screenType: "vagas" },
      { title: "Projetos", icon: FolderKanban, path: "/controle-projetos", screenType: "projetos" },
      { title: "Agências", icon: Building2, path: "/agencias", screenType: "agencias" },
      { title: "Radar de Perfis", icon: Radar, path: "/radar-agencias", screenType: "radar_agencias" },
      { title: "Empresas", icon: Factory, path: "/empresas", screenType: "empresas" },
      { title: "LinkedIn", icon: Linkedin, path: "/linkedin", screenType: "linkedin" },
      { title: "Atualizações do WhatsApp", icon: MessageCircle, path: "/atualizacoes-whatsapp", screenType: "atualizacoes_whatsapp" },
      { title: "Atividades", icon: HeartHandshake, path: "/atividades", screenType: "atividades", badgeKey: "atividades_pendentes" },
      { title: "Campanhas", icon: Mail, path: "/campanhas", screenType: "campanhas" },
      { title: "Financeiro", icon: Landmark, path: "/financeiro", screenType: "financeiro" },
    ],
  },
  {
    label: "Avaliações",
    items: [
      { title: "Por Projeto", icon: FileText, path: "/avaliacoes/pos-projeto", screenType: "avaliacoes_pos_projeto" },
      { title: "Perfil Freelancer", icon: UserCheck, path: "/avaliacoes/perfil-freelancer", screenType: "avaliacoes_perfil_freelancer" },
      { title: "Formulários", icon: ClipboardList, path: "/avaliacoes/formularios", screenType: "formularios_avaliacao" },
    ],
  },
  {
    label: "Agência",
    items: [
      { title: "Dashboard", icon: Home, path: "/agencia/dashboard", screenType: "dashboard_agencia" },
      { title: "Avaliações", icon: Star, path: "/agencia/avaliacoes", screenType: "avaliacoes_agencia" },
      { title: "Usuários", icon: Users, path: "/agencia/usuarios", screenType: "gestao_usuarios_agencia" },
    ],
  },
  {
    label: "Sistema",
    items: [
      { title: "Configurações", icon: Settings, path: "/configuracoes", screenType: "configuracoes" },
      { title: "Gestão de Perfis", icon: ShieldCheck, path: "/gestao-perfis", screenType: "gestao_perfis_usuarios" },
      { title: "Pesos da Recomendação", icon: Sliders, path: "/admin/recomendacao-pesos", screenType: "recomendacao_pesos" },
      { title: "Completude de Dados", icon: Gauge, path: "/admin/completude-dados", screenType: "completude_dados" },
    ],
  },
];

const CORISCO_LOGO = "/corisco-logo.png";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function AppSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  // Só oferece a troca quando a versão nova está publicada em /novo/.
  const { data: versaoNova = false } = useQuery({
    queryKey: ["versao-disponivel", "nova"],
    queryFn: () => versaoDisponivel("nova"),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const { data: reconduzida = null } = useQuery({
    queryKey: ["versao-reconduzida", user?.id],
    queryFn: versaoReconduzida,
    enabled: versaoNova && Boolean(user),
    staleTime: Infinity,
    retry: false,
  });
  useEffect(() => {
    if (reconduzida === "nova") seguirVersaoReconduzida(reconduzida);
  }, [reconduzida]);
  // Nome + branding de agência vêm do mesmo hook de permissões já disparado a
  // cada navegação (evita um GET /profiles extra por página).
  const { screens, fullName, profileName, agenciaLogoPath, agenciaCor } = useUserPermissions();
  const { data: atividadesPendentesCount = 0 } = useActivityPendingCount();

  const userName = fullName || user?.email?.split("@")[0] || "Usuário";

  const badgeCounts: Record<string, number> = {
    atividades_pendentes: atividadesPendentesCount,
  };

  // Filtra por permissão e descarta seções que ficaram sem itens.
  const allowedSections = menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => screens.includes(item.screenType)),
    }))
    .filter((section) => section.items.length > 0);

  const isActivePath = (path: string) => location.pathname === path;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center border-b border-sidebar-border px-3 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:items-center">
        <div className="flex items-center gap-2 overflow-hidden">
          {/* Marca compacta só no rail colapsado */}
          <div className="hidden size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground font-extrabold group-data-[collapsible=icon]:flex">
            C
          </div>
          <img
            src={CORISCO_LOGO}
            alt="Corisco"
            className="h-7 w-auto group-data-[collapsible=icon]:hidden"
          />
          {agenciaLogoPath && (
            <>
              <span className="text-sidebar-foreground/40 text-lg font-light group-data-[collapsible=icon]:hidden">
                +
              </span>
              <img
                src={agenciaLogoPath}
                alt="Agência"
                className="h-7 max-w-[110px] object-contain group-data-[collapsible=icon]:hidden"
              />
            </>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        {allowedSections.map((section, index) => (
          <div key={section.label ?? `section-${index}`}>
            {index > 0 && <SidebarSeparator className="my-1" />}
            <SidebarGroup>
              {section.label && (
                <SidebarGroupLabel className="uppercase tracking-wide text-sidebar-foreground/55">
                  {section.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const active = isActivePath(item.path);
                    const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
                    // Branding de agência: quando há cor customizada, o item ativo
                    // usa essa cor no texto/ícone/barra em vez do roxo da marca.
                    const activeStyle =
                      active && agenciaCor
                        ? ({ color: agenciaCor, "--tw-shadow-color": agenciaCor } as React.CSSProperties)
                        : undefined;
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={item.title}
                          style={activeStyle}
                          className={cn(
                            "relative h-9",
                            "[&>svg]:text-muted-foreground",
                            // Barra de acento à esquerda: só aparece no item ativo.
                            "before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r-full before:bg-transparent before:content-['']",
                            "data-[active=true]:bg-sidebar-primary/10 data-[active=true]:text-sidebar-primary data-[active=true]:font-medium data-[active=true]:shadow-xs",
                            "data-[active=true]:before:bg-current [&>svg]:data-[active=true]:text-current",
                          )}
                        >
                          <Link to={item.path}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                        {badgeCount > 0 && (
                          <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                            {badgeCount}
                          </SidebarMenuBadge>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:p-0!"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-sidebar-primary/10 text-sidebar-primary text-sm font-semibold">
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col text-left leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-medium">{userName}</span>
                <span className="truncate text-xs text-sidebar-foreground/60">
                  {user?.email || ""}
                </span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-2">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{userName}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
                {profileName && (
                  <Badge variant="secondary" className="w-fit text-xs">
                    {profileName}
                  </Badge>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {versaoNova && (
              <DropdownMenuItem onClick={() => void trocarVersao("nova")} className="cursor-pointer">
                <Sparkles className="mr-2 h-4 w-4" />
                <span>Voltar para a versão nova</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => supabase.auth.signOut()}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sair</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
